import styled from 'styled-components';
import { useState, useEffect } from 'react';
import { BlankSpace } from '../genericComponents/layouts';
import { priceFormat, roundPrice } from '../priceFormat';
import YearlyPrices from '../models/yearlyPrices.model';
import EquipmentPrices from '../models/equipmentPrices.model';
import Taxes from '../models/taxes.model';
import infoImg from '../assets/img/info.svg';
import {
  Text4, Text4M, Text3, Text3M, Text4B,
} from '../genericComponents/texts';
import ArrowDown2 from '../assets/img/ArrowDown2.svg';
import ArrowUp2 from '../assets/img/ArrowUp2.svg';
import VersionCompPrices from '../models/versionCompPrices.model';
import MarketPriceRange from './MarketPriceRange';
import RegularPrices from '../models/regularPrices.model';
import Publications from '../models/publications.model';
import logoAf from '../assets/img/logoAf.svg';
import { trfLandingPageUrl } from '../config/config';

interface Props {
  // eslint-disable-next-line @typescript-eslint/ban-types
  setAutofactStampVisible: Function;
  vehicle:{
    completeName:string;
    autofactPrice:number;
    maxPrice:number;
    minPrice:number;
    adjustSpecificVersion:number;
    year:number;
    equipment:string;
    taxes: Taxes;
    version: string;
  }
  basePrices: YearlyPrices[]
  equipmentPrices: EquipmentPrices[]
  mileage:{
    adjustPrice:number;
    vehicleMilage:number;
    referenceMileage:number;
  }
  priceNew:number | null
  adjustRegion: number
  purchaseAvgPrice: number | null
  region:{
    adjustPrice:number;
    regionName:string;
  }
  versionCompPrices: VersionCompPrices[]
  marketPrices:{
    regularPrice: RegularPrices;
  }
  publications: Publications;
}

const Mensajes = {
  clientPrice: 'No se ha encontrado precio Autofact.',
  avgPrice: 'Modelo no dispone de precio parte de pago (más de 15 años antigüedad)',
  minPrice: 'No se ha encontrado precio mínimo.',
  maxPrice: 'No se ha encontrado precio máximo.',
  rules: 'No se ha encontrado',
  avgTransferences: 'No encontramos información de precio de ventas reales.',
  priceNew: 'No disponible',
  noDisponible: 'No disponible',
};

export default function HeroSection({
  basePrices, vehicle, equipmentPrices, mileage, priceNew, adjustRegion, purchaseAvgPrice, region, versionCompPrices, marketPrices, publications, setAutofactStampVisible,
}:Props) {
  const [showTransferencesPrice, setShowTransferencesPrice] = useState(false);

  const currentBasePrice = basePrices.find((r) => r.ano_auto === vehicle.year)?.valor_base;
  const adjustEquipment = equipmentPrices.find((r) => {
    const concatenatedValue = `${r.version_m} ${r.equip}`;
    return concatenatedValue === vehicle.version;
  })?.adjust_value ?? 0;
  const avgTransferences = versionCompPrices.find((r) => r.version_comp === vehicle.version)?.promedio_transferencias || 0;
  const referenceMileage = mileage.referenceMileage ? mileage.referenceMileage : 0;
  const { autofactPrice, taxes, adjustSpecificVersion } = vehicle;
  const { adjustPrice: mileageAdjustPrice } = mileage;
  const { adjustPrice: regionAdjustPrice } = region;

  const finalAdjustSpecificVersion = adjustSpecificVersion + adjustEquipment;

  useEffect(() => {
    let diff = 0;
    if (avgTransferences) {
      diff = avgTransferences > autofactPrice ? avgTransferences - autofactPrice : autofactPrice - avgTransferences;
      if (avgTransferences > vehicle.minPrice && avgTransferences < vehicle.maxPrice) {
        setShowTransferencesPrice(true);
      } else if (diff < 500000) {
        setShowTransferencesPrice(true);
      } else {
        setShowTransferencesPrice(false);
      }
    } else {
      setShowTransferencesPrice(false);
    }
  }, [avgTransferences]);

  return (
    <Container>
      <PaddingContainer>

        <SubCont9>
          <ImportantSettingsContainerPriceAf>
            <ImportantSettingsSubCont1>
              <SubCont4>
                <TitlePrice>Precio de venta Autofact:</TitlePrice>
                <TextImportantSettings>
                  {autofactPrice !== 0
                    ? `$${priceFormat(roundPrice(autofactPrice, 5))}`
                    : (<Text4B>{Mensajes.clientPrice}</Text4B>)}
                </TextImportantSettings>
              </SubCont4>
            </ImportantSettingsSubCont1>
            <ImportantSettingsSubCont2>
              <AutoIconText
                text="Precio recomendado para el vehículo en condiciones normales."
                text2=""
                icon={infoImg}
              />
            </ImportantSettingsSubCont2>
          </ImportantSettingsContainerPriceAf>

          <ImportantSettingsContainer>
            <ImportantSettingsSubCont1>
              <BlankSpace width="10px" />
              <SubCont4>
                <TitlePrice>Promedio ventas reales:</TitlePrice>
                <BlankSpace height="5px" />
                <TextImportantSettings>
                  {showTransferencesPrice
                    ? `$${priceFormat(roundPrice(avgTransferences, 5))}`
                    : (
                      <SubCont4>
                        <Text4B>{Mensajes.avgTransferences}</Text4B>
                      </SubCont4>
                    )}
                </TextImportantSettings>
              </SubCont4>
            </ImportantSettingsSubCont1>
            <ImportantSettingsSubCont2>
              <AutoIconTextTrf
                text="Precio promedio en base a transferencias realizadas últimos 6 meses."
                text2="Autofact realiza el 30% de las transferencias de dominio en Chile a través de su sistema online."
                icon={infoImg}
              />
            </ImportantSettingsSubCont2>
          </ImportantSettingsContainer>

          <ImportantSettingsContainer>
            <ImportantSettingsSubCont1>
              <SubCont4>
                <TitlePrice>Precio en parte de pago:</TitlePrice>
                <TextImportantSettings>
                  {purchaseAvgPrice
                    ? `$${priceFormat(roundPrice(purchaseAvgPrice, 5))}`
                    : (
                      <SubCont4>
                        <Text4B>Modelo no dispone de precio parte de pago </Text4B>
                        <Text4>(más de 15 años antigüedad)</Text4>
                      </SubCont4>
                    )}
                </TextImportantSettings>
              </SubCont4>
            </ImportantSettingsSubCont1>
            <ImportantSettingsSubCont2>
              <AutoIconText
                text="Precio recomendado del vehículo para toma en concesionarios o compraventas."
                text2=""
                icon={infoImg}
              />
            </ImportantSettingsSubCont2>
          </ImportantSettingsContainer>

        </SubCont9>

        <SubCont9>
          <SettingCardAdjustPrices>
            <TitlePrice>Resumen y ajustes del precio</TitlePrice>
            <BasePrice>
              <ItemTitle text="Precio base" text2={`$${priceFormat(roundPrice(currentBasePrice || 0, 5))}`} />
            </BasePrice>
            <SubCont4>
              <ItemSettings
                key="1"
                text="Ajuste según versión específica"
                text2={`$${priceFormat(roundPrice(finalAdjustSpecificVersion, 5))}`}
                // eslint-disable-next-line no-nested-ternary
                icon={finalAdjustSpecificVersion > 0 ? ArrowUp2 : (finalAdjustSpecificVersion < 0 ? ArrowDown2 : null)}
                // eslint-disable-next-line no-nested-ternary
                textColor={finalAdjustSpecificVersion > 0 ? 'green' : (finalAdjustSpecificVersion < 0 ? 'red' : '#3D5176')}
                showIcon={finalAdjustSpecificVersion !== 0}
              />
              <ItemSettings
                key="2"
                text="Ajuste según kilometraje de referencia"
                text2={`$${priceFormat(roundPrice(mileageAdjustPrice, 5))}`}
                // eslint-disable-next-line no-nested-ternary
                icon={mileageAdjustPrice > 0 ? ArrowUp2 : (mileageAdjustPrice < 0 ? ArrowDown2 : null)}
                // eslint-disable-next-line no-nested-ternary
                textColor={mileageAdjustPrice > 0 ? 'green' : (mileageAdjustPrice < 0 ? 'red' : '#3D5176')}
                showIcon={mileageAdjustPrice !== 0}
              />
              <ItemSettings
                key="3"
                text={`Ajuste según ubicación ${region.regionName ? `(${region.regionName})` : ''}`}
                text2={`$${priceFormat(roundPrice(adjustRegion, 5))}`}
                // eslint-disable-next-line no-nested-ternary
                icon={regionAdjustPrice > 0 ? ArrowUp2 : (regionAdjustPrice < 0 ? ArrowDown2 : null)}
                // eslint-disable-next-line no-nested-ternary
                textColor={regionAdjustPrice > 0 ? 'green' : (regionAdjustPrice < 0 ? 'red' : '#3D5176')}
                showIcon={regionAdjustPrice !== 0}
              />
            </SubCont4>
            <RecommendedPrice>
              <ItemTitle text="Precio de venta recomendado:" text2={autofactPrice !== 0 ? `$${priceFormat(roundPrice(autofactPrice, 5))}` : '0'} />
            </RecommendedPrice>
          </SettingCardAdjustPrices>

          <SettingCardReferenceValuesGroup>
            <CardReferenceValues
              title="Kilometraje de referencia:"
              value={`${priceFormat(referenceMileage)}`}
            />
            <CardReferenceValues
              title="Tasación fiscal:"
              value={`${taxes.appraisal_value && taxes.appraisal_value > 0 ? `$${priceFormat(taxes.appraisal_value)}` : Mensajes.noDisponible}`}
            />
            <CardReferenceValues
              title="Valor permiso de circulación:"
              value={`${taxes.registration_value && taxes.registration_value > 0 ? `$${priceFormat(taxes.registration_value)}` : Mensajes.noDisponible}`}
            />
            <CardReferenceValues
              title="Precio cero kilómetro:"
              value={`${priceNew ? `$${priceFormat(priceNew)}` : Mensajes.priceNew}`}
            />
          </SettingCardReferenceValuesGroup>
        </SubCont9>

        <SubCont9>
          <SettingCardAdjustPrices>
            <TitlePrice>Rangos de precios de mercado</TitlePrice>
            <MarketPriceRange
              prices={marketPrices.regularPrice}
              publications={publications}
              avgTransferences={avgTransferences}
              showTransferencesPrice={showTransferencesPrice}
            />
          </SettingCardAdjustPrices>
        </SubCont9>

        <SubCont9>
          <Disclaimer>
            <AutoIconTextContainer>
              <img src={infoImg} width={15} height={15} alt="" />
              <BlankSpace width="30px" />
              <Text3>
                <ul>
                  <li>
                    <Text3M>Precio base: </Text3M>
                    Es el valor de mercado de un vehículo sin considerar sus opciones de equipamiento específicas.
                  </li>
                  <li>
                    <Text3M>Km. de referencia: </Text3M>
                    Es el kilometraje que se espera para un vehículo en un período de tiempo específico para un segmento determinado.
                  </li>
                  <li>
                    <Text3M>Ajuste según versión específica: </Text3M>
                    Se aplica cuando la versión del vehículo consultado distintas opciones de equipamiento u otras diferencias que influyen en su valor de mercado.
                  </li>
                  <li>
                    <Text3M>Ajuste según kilometraje de referencia: </Text3M>
                    Utiliza como kilometraje de referencia el promedio de kilometraje de vehículos con similares características: en antigüedad y segmento.
                  </li>
                  <li>
                    <Text3M>Ajuste por ubicación geográfica: </Text3M>
                    Ajuste aplicado en base a la diferencia de precios de vehículos similares en antigüedad y segmento publicados en la región seleccionada, según su valor de mercado general.
                  </li>
                </ul>
              </Text3>
            </AutoIconTextContainer>
          </Disclaimer>
          <SubCont1>
            <img src={logoAf} width={36} height={23} alt="" />
            <SubCont3 onClick={() => setAutofactStampVisible(true)}>
              <Text4M>Sello Autofact</Text4M>
              <LearnMore>¿Quieres saber más?</LearnMore>
            </SubCont3>
          </SubCont1>
        </SubCont9>

      </PaddingContainer>
    </Container>
  );
}

function ItemSettings({
  text,
  text2,
  icon,
  textColor,
  showIcon,
}: { text: string, text2: string, icon: string, textColor: string, showIcon: boolean; }) {
  return (
    <div>
      <Table>
        <tbody>
          <tr>
            <Tabletd>
              <Text3>{text}</Text3>
            </Tabletd>
            <TabletdRight style={{ width: '20px' }}>
              { showIcon ? <img src={icon} width={15} height={15} alt="" /> : ''}
            </TabletdRight>
            <TabletdRight style={{ width: '30%' }}>
              <strong style={{ color: textColor }}>{text2}</strong>
            </TabletdRight>
          </tr>
        </tbody>
      </Table>
    </div>
  );
}

function ItemTitle({ text, text2 }: { text: string, text2: string }) {
  return (
    <ContainerItemSettings>
      <Text4M>{text}</Text4M>
      <Text4M>{text2}</Text4M>
    </ContainerItemSettings>
  );
}

function CardReferenceValues({
  title, value,
}: { title: string, value: string }) {
  return (
    <SettingCardReferenceValues>
      <CarFetailsSubCont2>
        <Text4>{title}</Text4>
      </CarFetailsSubCont2>
      <BlankSpace height="4px" />
      <Text4M>
        {value}
      </Text4M>
    </SettingCardReferenceValues>
  );
}

function AutoIconText({ text, text2, icon }: { text: string, text2: string | null, icon: string }) {
  return (
    <AutoIconTextContainer>
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        width: '20px',
        justifyContent: 'center',
        paddingTop: '2px',
      }}
      >
        <img src={icon} width={15} height={15} alt="" />
      </div>
      <BlankSpace width="15px" />
      <Text3>
        {text}
        <BlankSpace width="5px" />
        {text2}
      </Text3>
    </AutoIconTextContainer>
  );
}

function AutoIconTextTrf({ text, text2, icon }: { text: string, text2: string | null, icon: string }) {
  return (
    <AutoIconTextContainer>
      <img src={icon} width={15} height={15} alt="" />
      <BlankSpace width="15px" />
      <Text3>
        {text}
        <BlankSpace width="5px" height="5px" />
        {text2}
        <BlankSpace width="5px" height="5px" />
        <LinkButton href={trfLandingPageUrl} target="_blank" rel="noopener noreferrer">
          Transfiere este vehículo con Autofact
        </LinkButton>
      </Text3>
    </AutoIconTextContainer>
  );
}

const SubCont1 = styled.div`
  display: flex;
  justify-content:space-between;
  align-items: center;
  flex-direction: row;
  margin: 0px;
  @media (max-width: 700px){
    flex-direction: row;
    align-items: flex-start;
    justify-content: center;
    padding-bottom: 10px;
  }
`;

const SubCont3 = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 120px;
  margin-left: 0px;
`;

const LearnMore = styled.div`
  font-size: 12px;
  color: #3D5176;
  text-decoration: underline;
  &:hover {
    color: #FFA401;
    text-decoration: underline;
  }
`;

const SettingCardAdjustPrices = styled.div`
  margin: 10px;
  border: 1px solid #EBF1F5;
  border-radius: 10px;
  padding: 10px;
  width: 100%;
  @media (max-width: 700px){
    width: auto;
  }
`;

const SettingCardReferenceValuesGroup = styled.div`
  margin: 0px 40px 0px 0px;
  border-radius: 0px;
  padding: 0px;
  width: 46%;
  @media (max-width: 700px){
    width: auto;
  }
`;

const SettingCardReferenceValues = styled.div`
  margin: 10px;
  border: 1px solid #EBF1F5;
  border-radius: 10px;
  padding: 10px;
  width: 100%;
  @media (max-width: 700px){
    width: 100%;
  }
`;

const CarFetailsSubCont2 = styled.div`
  display: flex;
  flex-direction: row;
`;

const RecommendedPrice = styled.div`
  padding: 5px 5px;
  margin-top: 5px;
  border-radius: 5px;
  background: #EBF1F5;
`;

const BasePrice = styled.div`
  padding: 5px 5px;
  margin-top: 5px;
  border-radius: 5px;
  background: #FAFAFA;
`;

export const CardTitle = styled.label`
  font-size: 16px;
  color: #155892;
  font-family: 'Roboto medium';

  @media (max-width: 700px){
    font-size: 14px;
  }
`;

const ContainerItemSettings = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content:space-between;
  align-items: center;
  margin: 10px 0;
`;

const AutoIconTextContainer = styled.div`
  width: 100%;
  margin: 10px 0px;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  @media (max-width: 700px){
    flex-direction: row;
    align-items: flex-start;
  }
`;

const Container = styled.div`
  padding: 15px 0;
  @media (max-width: 700px){
    padding: 0px 15px;
  }
`;
const PaddingContainer = styled.div`
  padding: 0px 5px;
`;
const SubCont4 = styled.div`
  display: flex;
  flex-direction: column;
  @media (max-width: 700px){
    flex: 1;
  }
`;
const SubCont9 = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
  @media (max-width: 700px){
    flex-direction: column;
    justify-content: center;
  }
`;
const ImportantSettingsContainer = styled.div`
  margin: 10px;
  border: 1px solid #C5C5C5;
  background: #F6F6F6;
  border-radius: 10px;
  padding: 10px;
  width: 45%;
  @media (max-width: 700px){
    width: auto;
  }
`;
const ImportantSettingsContainerPriceAf = styled.div`
  margin: 10px;
  border: 1px solid #EBF1F5;
  border-radius: 10px;
  padding: 10px;
  width: 45%;
  background: #EBF1F5;
  border: 1px solid #8FABC3;
  @media (max-width: 700px){
    width: auto;
  }
`;
const ImportantSettingsSubCont1 = styled.div`
  flex-direction: row;
  margin-bottom: 0px;
  margin-top: 0px;
  justify-content: center;
  padding: 10px 0;
  min-height: 60px;
  @media (max-width: 700px){
    min-height: 40px;
  }
`;
const TextImportantSettings = styled.div`
  font-size: 25px;
  color: #3D5176;
  font-family: 'Roboto medium';
  @media (max-width: 700px){
    font-size: 16px;
  }
`;
const ImportantSettingsSubCont2 = styled.div`
  display: flex;
  flex-direction: column;
`;

const TitlePrice = styled.div`
  color: #0B3254;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const Disclaimer = styled.div`
  margin: 10px;
  border: 1px solid #EBF1F5;
  border-radius: 10px;
  padding: 10px;
  width: 96%;
  background: #EBF1F5;
  border: 1px solid #8FABC3;
  @media (max-width: 700px){
    width: auto;
  }
`;

const Table = styled.table`
  margin: 8px 0;
  border: none;
  width: 100%;
  @media (max-width: 700px){
    width: 100%;
  }
`;

const Tabletd = styled.td`
  font-size: 12px;
  color: #3D5176;
  font-family: 'Roboto medium';
  padding: 0px 0;
  @media (max-width: 700px){
    width: 10%;
    padding: 0px 0;
  }
`;
const TabletdRight = styled.td`
  font-size: 12px;
  color: #3D5176;
  font-family: 'Roboto medium';
  text-align: right;
  padding: 0px 0;
  @media (max-width: 700px){
    width: 10%;
    padding: 0px 0;
  }
`;
const LinkButton = styled.a`
  font-size: 12px;
  color: #3D5176;
  text-decoration: underline;
  &:hover {
    color: #FFA401;
    text-decoration: underline;
  }
`;
